import {
  CountrySetSchema,
  countrySetToList,
  DEFAULT_LOCALE,
  listToCountrySet,
  LOCALE_DIRECTION,
  type Locale,
} from '@aysar/contracts';
import { useId, useRef } from 'react';
import { Controller, useWatch, type Control, type FieldValues, type Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '@/shared/components/ui/Checkbox/Checkbox';
import { ChoiceCards } from '@/shared/components/selection/ChoiceCards/ChoiceCards';
import { Combobox } from '@/shared/components/selection/Combobox/Combobox';
import { CountrySelect } from '@/shared/components/selection/CountrySelect/CountrySelect';
import { CountrySetField } from '@/shared/components/selection/CountrySetField/CountrySetField';
import { DurationInput } from '@/shared/components/scalar/DurationInput/DurationInput';
import { HeightInput } from '@/shared/components/scalar/HeightInput/HeightInput';
import { MoneyInput } from '@/shared/components/scalar/MoneyInput/MoneyInput';
import { PatternSelect } from '@/shared/components/scalar/PatternSelect/PatternSelect';
import { PercentInput } from '@/shared/components/scalar/PercentInput/PercentInput';
import { ColorInput } from '@/shared/components/ui/ColorInput/ColorInput';
import { DatePicker } from '@/shared/components/ui/DatePicker/DatePicker';
import { DateTimeInput } from '@/shared/components/ui/DateTimeInput/DateTimeInput';
import { Input } from '@/shared/components/ui/Input/Input';
import { JsonEditor } from '@/shared/components/structured/JsonEditor/JsonEditor';
import { KeyValueEditor } from '@/shared/components/structured/KeyValueEditor/KeyValueEditor';
import { MarkdownEditor } from '@/shared/components/structured/MarkdownEditor/MarkdownEditor';
import { type KeyValueScalar } from '@/shared/components/structured/types/key-value.types';
import { type StructuredIssue } from '@/shared/components/structured/types/structured-issue.types';
import { Label } from '@/shared/components/ui/Label/Label';
import { MediaPicker } from '@/shared/components/media/MediaPicker/MediaPicker';
import { MultiSelect } from '@/shared/components/selection/MultiSelect/MultiSelect';
import { ToggleGroup } from '@/shared/components/selection/ToggleGroup/ToggleGroup';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/Select/Select';
import { Switch } from '@/shared/components/ui/Switch/Switch';
import { Textarea } from '@/shared/components/ui/Textarea/Textarea';
import { useDirectionContext } from '@/shared/theme/DirectionProvider/DirectionProvider';

import { FieldTokenRail } from '../FieldTokenRail/FieldTokenRail';
import { SlugField } from '../SlugField/SlugField';
import { insertToken } from '../insert-token/insert-token.util';
import {
  FORM_BOOLEAN_ROW_CLASSES,
  FORM_CAPTION_CLASSES,
  FORM_DESCRIPTION_CLASSES,
  FORM_LABEL_CLASSES,
  FORM_LOCALE_MARK_CLASSES,
  FORM_MESSAGE_CLASSES,
  FORM_REQUIRED_MARK_CLASSES,
} from '../dynamic-form.tokens';
import { fieldErrorMessage } from '../field-error-message/field-error-message';
import { fieldCellClasses, fieldColSpan } from '../resolve-field-layout/resolve-field-layout';
import { namesItself } from '../self-naming/self-naming';
import { structuredIssuesOf } from '../structured-issues/structured-issues';
import { type FormColumns, type LeafFieldDef } from '../types/dynamic-form.types';

const isStringValue = (candidate: unknown): candidate is string => typeof candidate === 'string';

/**
 * Narrows a form value to the object map `ToggleGroup` edits.
 *
 * `unknown` at the boundary is what U4 permits: RHF hands back whatever the form
 * holds, and the control keeps the keys it does not know about, so the narrowing
 * stops at "is this an object".
 */
function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * Narrows a form value to what the scalar number controls accept.
 *
 * `unknown` at the boundary is exactly what U4 permits: RHF hands back whatever
 * the form holds. The distinction that must survive is `null` vs `undefined` —
 * "kept forever" and "nothing here yet" are different answers, and a `?? 0`
 * or a `?? undefined` would collapse them into one.
 */
function asScalarNumber(value: unknown): number | null | undefined {
  if (value === null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return undefined;
}

/**
 * Narrows a form value to a media id.
 *
 * `unknown` at the boundary is what U4 permits: RHF hands back whatever the form
 * holds. Anything that is not a non-empty string is "nothing chosen" — including
 * `''`, which is what a half-migrated setting or a `text`-controlled predecessor
 * would leave behind, and which the picker must not treat as an id it can
 * resolve.
 */
function asMediaId(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * Narrows a form value to the map `KeyValueEditor` edits.
 *
 * `unknown` at the boundary is what U4 permits and what this is: RHF hands back
 * whatever the form holds, and giving it a type is precisely this function's job.
 * A non-object (a half-migrated setting, a `null`) becomes "empty" rather than a
 * crash, and the entries that are not scalars are dropped rather than rendered as
 * `[object Object]`.
 */
function asKeyValueMap(value: unknown): Record<string, KeyValueScalar> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return {};

  const out: Record<string, KeyValueScalar> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (typeof entry === 'string' || typeof entry === 'number' || typeof entry === 'boolean') {
      out[key] = entry;
    }
  }

  return out;
}

/**
 * The form's own nested errors, translated once, for a structured control.
 *
 * The path stays; only the message is resolved here — a Zod code becomes kit
 * copy and a server message passes through verbatim (U8), exactly as it does for
 * every other kind's single message.
 */
function translatedIssues(error: unknown, t: (key: string) => string): readonly StructuredIssue[] {
  return structuredIssuesOf(error)
    .filter((issue) => issue.path.length > 0 && issue.message !== null)
    .map((issue) => ({
      path: issue.path,
      message:
        issue.message?.kind === 'key' ? t(issue.message.i18nKey) : (issue.message?.text ?? ''),
    }));
}

export interface FieldRendererProps<TValues extends FieldValues> {
  /**
   * A LEAF, never an object group: this component renders exactly one
   * `Controller`, and a group is N of them plus a fieldset. Narrowing the prop
   * is what keeps the switch below exhaustive over kinds that actually have a
   * control, rather than acquiring a `default:` arm that draws a text box for
   * the one kind it cannot express.
   */
  field: LeafFieldDef;
  /** The resolved form path — already locale-expanded for a translatable field. */
  path: Path<TValues>;
  control: Control<TValues>;
  /** The section's column ceiling — the field's own span is clamped to it. */
  columns: FormColumns;
  /** Which translation a translatable field is currently editing. */
  locale: Locale;
  /**
   * For a slug field: the path of the field it derives from. A PATH, not a
   * value — the source lives in a sibling `Controller`, so a value read once
   * during render goes stale the moment that sibling changes and the Regenerate
   * button would rebuild the key from whatever the name used to be.
   */
  sourcePath?: Path<TValues>;
  /** Called when a slug field is edited by hand, so it stops tracking its source. */
  onSlugManualEdit?: () => void;
  /** Called after any value change, so the form can drive a dependent slug. */
  onAfterChange?: (path: string, value: unknown) => void;
  /**
   * A control saying its own text is unreadable, so it emitted no value. Only
   * `json` has this state today; the prop is on the renderer rather than on the
   * kind because the form addresses it by PATH and only this layer knows one.
   */
  onParseFailureChange?: (path: string, isUnparsed: boolean) => void;
}

/**
 * One labelled control.
 *
 * Every kind goes through RHF's `Controller` rather than `register`. Uniformity
 * is the point: a Radix `Select`, a `Switch` and a `DatePicker` are not native
 * inputs and cannot be registered, and having half the fields on one mechanism
 * and half on another is how a form ends up with a control that never marks
 * itself dirty.
 *
 * Validation copy is resolved through `fieldErrorMessage` instead of showing
 * `error.message`: Zod's own strings are English constants inside the library,
 * and they would appear untranslated under an Arabic label (U8).
 */
export function FieldRenderer<TValues extends FieldValues>({
  field,
  path,
  control,
  columns,
  locale,
  sourcePath,
  onSlugManualEdit,
  onAfterChange,
  onParseFailureChange,
}: FieldRendererProps<TValues>) {
  const { t } = useTranslation('uiKit');
  // Nullable on purpose: the renderer is mounted without a provider in specs,
  // and the default locale's direction is what `<html dir>` starts from too.
  const direction = useDirectionContext()?.direction ?? LOCALE_DIRECTION[DEFAULT_LOCALE];
  const controlId = useId();
  const describedById = useId();
  const counterId = useId();
  const messageId = useId();
  /**
   * The text element a token chip writes into.
   *
   * Only the two text kinds ever fill it, but the ref is declared
   * unconditionally — a hook behind a branch is not a hook.
   */
  const tokenTargetRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const tokens = field.kind === 'text' || field.kind === 'textarea' ? field.tokens : undefined;
  const hasTokens = tokens !== undefined && tokens.length > 0;
  const localeMark = field.isTranslatable === true ? t(`locale.${locale}`) : null;
  /**
   * What a self-naming control announces itself as.
   *
   * Those kinds compose their own accessible name from this string, so the
   * locale has to travel INSIDE it — for every other kind it rides in the
   * `<Label>` instead. Either way the answer a screen reader gives is the same
   * as the chip on screen.
   */
  const controlLabel = localeMark === null ? field.label : `${field.label} — ${localeMark}`;

  // Subscribed, so a slug field re-renders when its source changes. Watching
  // `path` when there is no source keeps the hook unconditional at no cost —
  // this field re-renders on its own change anyway.
  const watchedSource: unknown = useWatch({ control, name: sourcePath ?? path });
  const sourceValue = typeof watchedSource === 'string' ? watchedSource : '';

  return (
    <Controller
      control={control}
      name={path}
      render={({ field: rhf, fieldState }) => {
        const isInvalid = fieldState.error !== undefined;
        const message = fieldErrorMessage(
          fieldState.error,
          field.isRequired === true,
          field.invalidMessage,
          // What the control is actually holding: it is what separates "you left
          // this empty" from "that is below the floor", and a counted bound from
          // a measured one.
          rhf.value,
        );
        const isDisabled = field.isDisabled === true;

        const change = (value: unknown) => {
          rhf.onChange(value);
          onAfterChange?.(path, value);
        };

        /**
         * Writes a token where the operator was typing, and puts them back
         * there.
         *
         * The caret is restored on the next frame rather than immediately: the
         * value has to reach the DOM before a range in it means anything, and
         * without this an insert would leave the caret at the end of the box —
         * so a second chip could never land beside the first.
         */
        const insertTokenAtCaret = (token: string) => {
          const element = tokenTargetRef.current;
          const next = insertToken(
            String(rhf.value ?? ''),
            element?.selectionStart ?? null,
            element?.selectionEnd ?? null,
            token,
          );

          change(next.value);

          requestAnimationFrame(() => {
            const target = tokenTargetRef.current;
            if (target === null) return;

            target.focus();
            target.setSelectionRange(next.caret, next.caret);
          });
        };

        // BOTH, joined — not one id that may point at nothing.
        //
        // The description and the error live in two elements, and this used to
        // name only the description's id: with an error and no description it
        // pointed at an element that does not exist, and with both it linked the
        // description and dropped the error. The message also carries
        // `role="alert"`, which announces it when it APPEARS; this is what a
        // screen reader reads when the operator comes back to the field later.
        const counterText =
          field.kind === 'textarea' && field.counter !== undefined
            ? field.counter(String(rhf.value ?? ''))
            : null;

        const describedBy =
          [
            field.description === undefined ? null : describedById,
            counterText === null ? null : counterId,
            message === null ? null : messageId,
          ]
            .filter((id) => id !== null)
            .join(' ') || undefined;

        const renderControl = () => {
          const shared = {
            id: controlId,
            disabled: isDisabled,
            'aria-describedby': describedBy,
            // On EVERY kind, not on the one that happened to need it first.
            // `shouldFocusError` calls `field.ref.focus()`; a Controller field
            // whose ref callback never fires has nothing to focus, so an invalid
            // control is silently never reached — and there is no warning, no
            // type error and no failing spec to say so. Hoisting it here is what
            // stops the next kind from being added without it.
            //
            // COMPOSED, never replaced, where a token rail needs the element
            // too: dropping `rhf.ref` here would leave an invalid field with
            // nothing to focus, silently — the failure the comment above
            // records.
            ref: hasTokens
              ? (element: HTMLInputElement | HTMLTextAreaElement | null) => {
                  tokenTargetRef.current = element;
                  rhf.ref(element);
                }
              : rhf.ref,
            onBlur: rhf.onBlur,
          };

          switch (field.kind) {
            case 'textarea':
              return (
                <Textarea
                  {...shared}
                  value={String(rhf.value ?? '')}
                  rows={field.rows}
                  maxLength={field.maxLength}
                  placeholder={field.placeholder}
                  isInvalid={isInvalid}
                  onBlur={rhf.onBlur}
                  onChange={(event) => {
                    change(event.target.value);
                  }}
                />
              );

            case 'number':
              return (
                <Input
                  {...shared}
                  type="number"
                  // `?? ''` keeps the input controlled while the value is
                  // legitimately undefined — which is what "not yet answered"
                  // looks like for a number (see `buildDefaultValues`).
                  value={rhf.value === undefined || rhf.value === null ? '' : String(rhf.value)}
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  placeholder={field.placeholder}
                  isInvalid={isInvalid}
                  onBlur={rhf.onBlur}
                  onChange={(event) => {
                    const raw = event.target.value;
                    // An emptied numeric field is `undefined`, never 0 — the
                    // same distinction the defaults make.
                    change(raw === '' ? undefined : Number(raw));
                  }}
                />
              );

            case 'select':
              return (
                <Select
                  value={String(rhf.value ?? '')}
                  disabled={isDisabled}
                  onValueChange={change}
                >
                  <SelectTrigger
                    ref={rhf.ref}
                    id={controlId}
                    aria-describedby={describedBy}
                    aria-invalid={isInvalid}
                    onBlur={rhf.onBlur}
                  >
                    <SelectValue placeholder={field.placeholder ?? t('form.selectPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {field.options.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              );

            case 'combobox':
              return (
                <Combobox
                  ref={rhf.ref}
                  id={controlId}
                  size={field.size}
                  options={field.options}
                  groups={field.groups}
                  isCreatable={field.isCreatable}
                  validateCreate={field.validateCreate}
                  placeholder={field.placeholder}
                  searchPlaceholder={field.searchPlaceholder}
                  isDisabled={isDisabled}
                  isReadOnly={field.isReadOnly}
                  isInvalid={isInvalid}
                  aria-describedby={describedBy}
                  onBlur={rhf.onBlur}
                  // `'' ↔ null` exactly as the `select` arm does: react-hook-form
                  // holds the empty form value as `''`, and the control's "nothing
                  // chosen" is `null` because an empty string is a value.
                  value={rhf.value === '' || rhf.value == null ? null : String(rhf.value)}
                  onValueChange={(next) => {
                    change(next ?? '');
                  }}
                />
              );

            // Over `ChoiceCards` rather than a second composition on the raw
            // Radix primitive: that control already owns the group's accessible
            // name, its `aria-readonly`, the `''`-never-`undefined` controlled
            // value, the live region, and the ref-to-the-first-card that
            // `shouldFocusError` needs. It was reachable only through
            // `kind: 'custom'` until now; this is the arm that makes it a field.
            case 'radio-group':
              return (
                <ChoiceCards
                  ref={rhf.ref}
                  id={controlId}
                  aria-describedby={describedBy}
                  label={controlLabel}
                  items={field.options}
                  isDisabled={isDisabled}
                  isReadOnly={field.isReadOnly}
                  onBlur={rhf.onBlur}
                  // `'' ↔ null` as the `combobox` and `select` arms do — the
                  // empty form value is `''`, "nothing chosen" is `null`.
                  value={rhf.value === '' || rhf.value == null ? null : String(rhf.value)}
                  onValueChange={change}
                />
              );

            case 'multi-select':
              return (
                <MultiSelect
                  ref={rhf.ref}
                  id={controlId}
                  // Spelled out rather than spread: `shared` carries `disabled`,
                  // which this control names `isDisabled`. The description and
                  // the error text link through here, and this arm was the only
                  // kind in the portal that had dropped them.
                  aria-describedby={describedBy}
                  value={Array.isArray(rhf.value) ? rhf.value.filter(isStringValue) : []}
                  options={field.options}
                  groups={field.groups}
                  maxVisibleChips={field.maxVisibleChips}
                  maxSelected={field.maxSelected}
                  hasSelectAll={field.hasSelectAll}
                  isCreatable={field.isCreatable}
                  size={field.size}
                  placeholder={field.placeholder}
                  isDisabled={isDisabled}
                  isInvalid={isInvalid}
                  onBlur={rhf.onBlur}
                  onValueChange={change}
                />
              );

            // Every kind now takes a ref. `toggle-group`, `color` and `slug` were
            // the last three that did not — plain function components, so
            // `shouldFocusError` had nothing to call and an invalid one of those
            // was reported and never reached. Closed in P10.2d, each with a spec
            // that focuses through the ref rather than trusting the prop.
            case 'toggle-group':
              return (
                <ToggleGroup
                  ref={rhf.ref}
                  id={controlId}
                  aria-describedby={describedBy}
                  label={controlLabel}
                  items={field.items}
                  value={asRecord(rhf.value)}
                  size={field.size}
                  isDisabled={isDisabled}
                  isReadOnly={field.isReadOnly}
                  onBlur={rhf.onBlur}
                  onValueChange={change}
                />
              );

            case 'key-value':
              return (
                <KeyValueEditor
                  ref={rhf.ref}
                  id={controlId}
                  aria-describedby={describedBy}
                  label={controlLabel}
                  value={asKeyValueMap(rhf.value)}
                  valueKind={field.valueKind}
                  options={field.options}
                  catalogue={field.catalogue}
                  isKeyRestricted={field.isKeyRestricted}
                  maxRows={field.maxRows}
                  keyPlaceholder={field.keyPlaceholder}
                  valuePlaceholder={field.valuePlaceholder}
                  valueDir={field.valueDir}
                  columns={field.columns}
                  isImportable={field.isImportable}
                  size={field.size}
                  isDisabled={isDisabled}
                  isReadOnly={field.isReadOnly}
                  isInvalid={isInvalid}
                  // The nested errors the form ALREADY holds, addressed by key —
                  // until this phase they were flattened to one generic message
                  // under the whole editor, which told the operator that
                  // something in twelve rows was wrong.
                  issues={translatedIssues(fieldState.error, t)}
                  onBlur={rhf.onBlur}
                  onValueChange={change}
                />
              );

            case 'json':
              return (
                <JsonEditor
                  ref={rhf.ref}
                  // The one control that can hold a value it could not read.
                  // Without this the form validates the last document that
                  // parsed and reports success over the broken paste.
                  onParseFailureChange={(isUnparsed) => {
                    onParseFailureChange?.(path, isUnparsed);
                  }}
                  id={controlId}
                  label={controlLabel}
                  value={rhf.value}
                  minRows={field.rows}
                  aria-describedby={describedBy}
                  isDisabled={isDisabled}
                  isInvalid={isInvalid}
                  // The nested errors the form already holds, each addressed by
                  // its path into the value — so a schema failure names the
                  // member it is about rather than the whole document.
                  issues={translatedIssues(fieldState.error, t)}
                  onBlur={rhf.onBlur}
                  onValueChange={change}
                />
              );

            case 'markdown':
              return (
                <MarkdownEditor
                  ref={rhf.ref}
                  id={controlId}
                  label={controlLabel}
                  value={String(rhf.value ?? '')}
                  // A translation reads in ITS locale's direction: the Arabic
                  // body is RTL on an English interface and the other way round.
                  dir={field.isTranslatable === true ? LOCALE_DIRECTION[locale] : direction}
                  minRows={field.minRows}
                  aria-describedby={describedBy}
                  isDisabled={isDisabled}
                  isReadOnly={field.isReadOnly}
                  isInvalid={isInvalid}
                  onBlur={rhf.onBlur}
                  onValueChange={change}
                />
              );

            case 'switch':
              return (
                <Switch
                  ref={rhf.ref}
                  id={controlId}
                  checked={rhf.value === true}
                  disabled={isDisabled}
                  aria-describedby={describedBy}
                  onBlur={rhf.onBlur}
                  onCheckedChange={change}
                />
              );

            case 'checkbox':
              return (
                <Checkbox
                  ref={rhf.ref}
                  id={controlId}
                  checked={rhf.value === true}
                  disabled={isDisabled}
                  aria-describedby={describedBy}
                  onBlur={rhf.onBlur}
                  onCheckedChange={(checked) => {
                    change(checked === true);
                  }}
                />
              );

            case 'date':
              return (
                <DatePicker
                  {...shared}
                  // Calendar days, straight through: a date bound is a DAY, and
                  // the picker's own `min`/`max` speak the same `YYYY-MM-DD` its
                  // value does.
                  min={field.min}
                  max={field.max}
                  value={typeof rhf.value === 'string' ? rhf.value : null}
                  isInvalid={isInvalid}
                  onValueChange={change}
                />
              );

            case 'datetime':
              return (
                <DateTimeInput
                  {...shared}
                  // Instants straight through, in the same space as the value:
                  // the control is the one place a wall clock exists.
                  min={field.min}
                  max={field.max}
                  value={typeof rhf.value === 'string' ? rhf.value : null}
                  isInvalid={isInvalid}
                  onValueChange={change}
                />
              );

            case 'height':
              return (
                <HeightInput
                  ref={rhf.ref}
                  id={controlId}
                  aria-describedby={describedBy}
                  label={controlLabel}
                  // A height is a number too, so the same three states survive:
                  // a number, and `undefined` for "not yet said". Never `0`.
                  value={asScalarNumber(rhf.value)}
                  min={field.min}
                  max={field.max}
                  size={field.size}
                  isDisabled={isDisabled}
                  isReadOnly={field.isReadOnly}
                  isInvalid={isInvalid}
                  onBlur={rhf.onBlur}
                  onValueChange={change}
                />
              );

            case 'country-set':
              return (
                <CountrySetField
                  ref={rhf.ref}
                  id={controlId}
                  size={field.size}
                  isDisabled={isDisabled}
                  isReadOnly={field.isReadOnly}
                  isInvalid={isInvalid}
                  aria-describedby={describedBy}
                  onBlur={rhf.onBlur}
                  // The form holds the stored RECORD (`toggle-group`'s `asRecord`
                  // shape); the control speaks in codes. An untouched or foreign
                  // value reads as the empty set rather than crashing the row.
                  value={countrySetToList(CountrySetSchema.safeParse(rhf.value).data ?? {}) ?? []}
                  onValueChange={(next) => {
                    change(listToCountrySet(next));
                  }}
                />
              );

            case 'country':
              return (
                <CountrySelect
                  ref={rhf.ref}
                  id={controlId}
                  size={field.size}
                  placeholder={field.placeholder}
                  searchPlaceholder={field.searchPlaceholder}
                  isDisabled={isDisabled}
                  isReadOnly={field.isReadOnly}
                  isInvalid={isInvalid}
                  aria-describedby={describedBy}
                  onBlur={rhf.onBlur}
                  // `'' ↔ null`, exactly as the `combobox` arm does it: the form
                  // holds an untouched field as `''`, and "nothing chosen" is
                  // `null` because an empty string is a value a schema can see.
                  value={rhf.value === '' || rhf.value == null ? null : String(rhf.value)}
                  onValueChange={(next) => {
                    change(next ?? '');
                  }}
                />
              );

            case 'duration':
              return (
                <DurationInput
                  ref={rhf.ref}
                  id={controlId}
                  aria-describedby={describedBy}
                  label={controlLabel}
                  // Three states survive the boundary: a number, `null`
                  // (forever) and `undefined` (nothing yet). A `?? undefined`
                  // here would erase the difference the control exists to keep.
                  value={asScalarNumber(rhf.value)}
                  baseUnit={field.baseUnit}
                  min={field.min}
                  max={field.max}
                  units={field.units}
                  isNullable={field.isNullable}
                  size={field.size}
                  isDisabled={isDisabled}
                  isReadOnly={field.isReadOnly}
                  isInvalid={isInvalid}
                  onBlur={rhf.onBlur}
                  onValueChange={change}
                />
              );

            case 'percent':
              return (
                <PercentInput
                  ref={rhf.ref}
                  id={controlId}
                  aria-describedby={describedBy}
                  label={controlLabel}
                  value={asScalarNumber(rhf.value)}
                  min={field.min}
                  max={field.max}
                  decimals={field.decimals}
                  step={field.step}
                  size={field.size}
                  isDisabled={isDisabled}
                  isReadOnly={field.isReadOnly}
                  isInvalid={isInvalid}
                  onBlur={rhf.onBlur}
                  onValueChange={change}
                />
              );

            case 'money':
              return (
                <MoneyInput
                  ref={rhf.ref}
                  id={controlId}
                  aria-describedby={describedBy}
                  label={controlLabel}
                  value={asScalarNumber(rhf.value)}
                  currency={field.currency}
                  perUnit={field.perUnit}
                  min={field.min}
                  max={field.max}
                  decimals={field.decimals}
                  step={field.step}
                  size={field.size}
                  isDisabled={isDisabled}
                  isReadOnly={field.isReadOnly}
                  isInvalid={isInvalid}
                  onBlur={rhf.onBlur}
                  onValueChange={change}
                />
              );

            case 'pattern':
              return (
                <PatternSelect
                  ref={rhf.ref}
                  id={controlId}
                  aria-describedby={describedBy}
                  label={controlLabel}
                  value={String(rhf.value ?? '')}
                  presets={field.presets}
                  size={field.size}
                  isDisabled={isDisabled}
                  isReadOnly={field.isReadOnly}
                  isInvalid={isInvalid}
                  onBlur={rhf.onBlur}
                  onValueChange={change}
                />
              );

            case 'media':
              return (
                <MediaPicker
                  ref={rhf.ref}
                  id={controlId}
                  aria-describedby={describedBy}
                  // Self-naming: it renders a preview, an upload, a library
                  // button and a remove, so this names the group and each child
                  // composes its own name from it.
                  label={controlLabel}
                  value={asMediaId(rhf.value)}
                  kind={field.mediaKind}
                  source={field.source}
                  maxBytes={field.maxBytes}
                  size={field.size}
                  isDisabled={isDisabled}
                  isReadOnly={field.isReadOnly}
                  isInvalid={isInvalid}
                  onBlur={rhf.onBlur}
                  onValueChange={change}
                />
              );

            case 'color':
              return (
                <ColorInput
                  ref={rhf.ref}
                  id={controlId}
                  describedById={describedBy}
                  onBlur={rhf.onBlur}
                  // `ColorInput` composes its OWN accessible names from this —
                  // "Brand (colour picker)" and "Brand (hex value)" — because it
                  // renders two controls. That is also why the visible label
                  // above is not `htmlFor`-linked here: pointing one label at
                  // both would restore the ambiguity its own spec caught.
                  label={controlLabel}
                  value={String(rhf.value ?? '')}
                  disabled={isDisabled}
                  isInvalid={isInvalid}
                  onValueChange={change}
                />
              );

            case 'slug':
              return (
                <SlugField
                  ref={rhf.ref}
                  id={controlId}
                  describedById={describedBy}
                  onBlur={rhf.onBlur}
                  value={String(rhf.value ?? '')}
                  sourceValue={sourceValue}
                  hasSource={sourcePath !== undefined}
                  separator={field.separator}
                  maxLength={field.maxLength}
                  isLocked={field.isLockedAfterCreate === true && isDisabled}
                  isInvalid={isInvalid}
                  placeholder={field.placeholder}
                  onManualEdit={() => {
                    onSlugManualEdit?.();
                  }}
                  onChange={change}
                />
              );

            case 'custom':
              return field.render({
                id: controlId,
                name: path,
                value: rhf.value,
                onChange: change,
                onBlur: rhf.onBlur,
                isInvalid,
                isDisabled,
                describedById: describedBy,
              });

            default:
              return (
                <Input
                  {...shared}
                  // Only `text` and `password` reach the default arm, and a
                  // password is never anything else — so the caller's hint
                  // applies to `text` alone.
                  type={field.kind === 'text' ? (field.inputType ?? 'text') : 'password'}
                  value={String(rhf.value ?? '')}
                  maxLength={field.maxLength}
                  placeholder={field.placeholder}
                  isInvalid={isInvalid}
                  onBlur={rhf.onBlur}
                  onChange={(event) => {
                    change(event.target.value);
                  }}
                />
              );
          }
        };

        const labelNode = namesItself(field) ? (
          // A control that names itself gets a plain caption, not a second label
          // competing with the names it already composed — and for the
          // group-shaped ones (`toggle-group`, `key-value`) an `htmlFor` would
          // point at a `div`, which names nothing at all. The required marker is
          // drawn here rather than dropped, which is what this branch used to do.
          <p className={FORM_CAPTION_CLASSES}>
            {field.label}
            {field.isRequired === true ? (
              <span aria-hidden className={FORM_REQUIRED_MARK_CLASSES}>
                *
              </span>
            ) : null}
            {localeMark === null ? null : (
              <span className={FORM_LOCALE_MARK_CLASSES}>{localeMark}</span>
            )}
          </p>
        ) : (
          <Label
            htmlFor={controlId}
            isRequired={field.isRequired === true}
            className={FORM_LABEL_CLASSES}
          >
            {field.label}
            {/* INSIDE the label, so the locale joins the control's accessible
                name rather than merely sitting beside it: a translatable field
                now stands among fields that have no locale at all, and the tab
                strip alone no longer says which ones it governs. */}
            {localeMark === null ? null : (
              <span className={FORM_LOCALE_MARK_CLASSES}>{localeMark}</span>
            )}
          </Label>
        );

        // A boolean is a NAME and a state, on one line. Stacked, a 44px switch
        // sits alone under the sentence that explains it and reads as a control
        // that lost its label — which is what the first stills of this phase
        // showed, and what every other boolean surface in the kit already avoids.
        const isBoolean = field.kind === 'switch' || field.kind === 'checkbox';

        return (
          <div
            // The walk's grip on this cell, and the interleave proof: the DOM
            // order of these names must equal the author's order, and the
            // visual order must equal the DOM order in BOTH directions.
            data-field-name={field.name}
            data-col-span={String(fieldColSpan(field, columns))}
            data-translatable={field.isTranslatable === true ? 'true' : undefined}
            className={fieldCellClasses(field, columns)}
          >
            {isBoolean ? (
              <div className={FORM_BOOLEAN_ROW_CLASSES}>
                {labelNode}
                {renderControl()}
              </div>
            ) : (
              <>
                {labelNode}
                {renderControl()}
              </>
            )}

            {tokens === undefined || tokens.length === 0 ? null : (
              <FieldTokenRail
                tokens={tokens}
                // Names the FIELD it belongs to: a form can carry two rails, and
                // "Insert a value" twice tells a screen-reader user nothing
                // about which control they are about to write into.
                label={t('form.tokenRail', { field: controlLabel })}
                isDisabled={isDisabled}
                onInsert={insertTokenAtCaret}
              />
            )}

            {counterText === null ? null : (
              <p id={counterId} aria-live="polite" className={FORM_DESCRIPTION_CLASSES}>
                {counterText}
              </p>
            )}

            {field.description === undefined ? null : (
              <p id={describedById} className={FORM_DESCRIPTION_CLASSES}>
                {field.description}
              </p>
            )}

            {message === null ? null : (
              <p id={messageId} role="alert" className={FORM_MESSAGE_CLASSES}>
                {message.kind === 'key' ? t(message.i18nKey) : message.text}
              </p>
            )}
          </div>
        );
      }}
    />
  );
}
